import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/nova/components';
import { CopyIcon } from '../icons';
import { formatTtl, type DnsRecordView } from '../../lib/dns';
import { CUST_DNS_TYPE, CUST_DNS_TYPE_FALLBACK } from '../../../../theme/customizer-tokens';
import {
  List,
  Card,
  Top,
  Badge,
  Title,
  Cols,
  Col,
  ColLabel,
  ColRow,
  ColValue,
  CopyBtn,
} from './DnsRecordList.styles';

export interface DnsRecordListProps {
  records: DnsRecordView[];
  onCopy: (value: string) => void;
}

/** Mobile DNS records — one card per record (type badge + Host / Value / TTL). */
export const DnsRecordList = ({ records, onCopy }: DnsRecordListProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <List>
      {records.map((r, i) => {
        const tone = CUST_DNS_TYPE[r.type] ?? CUST_DNS_TYPE_FALLBACK;
        const fields = [
          { id: 'host', label: t('Host'), value: r.host, aria: t('Copy host') },
          { id: 'value', label: t('Value'), value: r.data, aria: t('Copy value') },
          { id: 'ttl', label: t('TTL (Seconds)'), value: formatTtl(r.ttl), aria: t('Copy TTL') },
        ];
        return (
          <Card key={`${r.type}-${r.host}-${i}`}>
            <Top>
              <Badge $color={tone.color} $bg={tone.bg}>
                <Text text={r.type.charAt(0)} tag="span" />
              </Badge>
              <Title text="{{type}} Record" placeholders={{ type: r.type }} tag="span" />
              <CopyBtn type="button" aria-label={t('Copy value')} onClick={() => onCopy(r.data)}>
                <CopyIcon size={16} />
              </CopyBtn>
            </Top>
            <Cols>
              {fields.map((f) => (
                <Col key={f.id}>
                  <ColLabel text={f.label} tag="span" />
                  <ColRow>
                    <ColValue text={f.value} tag="span" title={f.value} />
                    <CopyBtn type="button" aria-label={f.aria} onClick={() => onCopy(f.value)}>
                      <CopyIcon size={14} />
                    </CopyBtn>
                  </ColRow>
                </Col>
              ))}
            </Cols>
          </Card>
        );
      })}
    </List>
  );
};

export default DnsRecordList;